import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogContainer from './side_menu/components/DialogContainer';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { PlayerContext } from './PlayerContext';
import { analyticsManager } from '../index';
import {
    findNearbyChainRestaurants,
    fitMapToChainRestaurants,
    geocodeCityOrPostalCode,
    getChainRestaurantsCenter,
    getUserGeolocation,
    getChainRestaurantPosArea,
    getValidChainRestaurants,
} from './chainRestaurantUtils';

function ChainRestaurantPlaceDialog({ opened, onClose, onPlaceSelected, story, storyId }) {
    const { t } = useTranslation();
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const primaryColor = useContext(PlayerContext).get("primaryColor") ?? story?.primaryColor;
    const secondaryColor = useContext(PlayerContext).get("secondaryColor") ?? story?.secondaryColor;
    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const fromDemoFeed = useMemo(() => {
        try {
            return new URLSearchParams(window.location.search).get("fromDemoFeed") === "true";
        } catch (e) {
            return false;
        }
    }, []);
    const [locationQuery, setLocationQuery] = useState('');
    const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchCenter, setSearchCenter] = useState(null);

    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    const chooseButtonLabelRef = useRef(t('ChainChooseButton'));
    const primaryColorRef = useRef(primaryColor);

    onPlaceSelectedRef.current = onPlaceSelected;
    chooseButtonLabelRef.current = t('ChainChooseButton');
    primaryColorRef.current = primaryColor;

    const allRestaurants = useMemo(
        () => getValidChainRestaurants(story?.chainRestaurants),
        [story?.chainRestaurants]
    );

    const mapRestaurants = hasSearched ? nearbyRestaurants : allRestaurants;
    const displayedRestaurants = hasSearched ? nearbyRestaurants : allRestaurants;
    const dialogHorizontalInset = 32 * sizeMultiplier;
    const dialogMaxWidth = Math.max(0, storyDimensions.containerWidth - dialogHorizontalInset);
    const dialogContentWidth = dialogMaxWidth;
    const dialogPadding = 16 * sizeMultiplier;
    const mapMarkerScale = 1.2 * sizeMultiplier;
    const infoWindowMaxWidth = Math.max(120, Math.round(Math.min(220 * sizeMultiplier, dialogContentWidth)));

    const selectRestaurant = useCallback((restaurant, origin) => {
        if (origin === 'map_info_window') {
            analyticsManager.logChainRestaurantChooseMapTag(storyId);
        } else {
            analyticsManager.logChainRestaurantChooseListTag(storyId);
        }
        onPlaceSelectedRef.current(restaurant);
    }, [storyId]);

    function handleClose() {
        analyticsManager.logOpenPopupTag(storyId, 'chain_restaurant_dialog', false);
        onClose();
    }

    const buildInfoWindowContent = useCallback((restaurant) => {
        const sm = sizeMultiplier;
        const container = document.createElement('div');
        container.style.fontFamily = 'sans-serif';
        container.style.fontSize = `${14 * sm}px`;
        container.style.lineHeight = '1.4';
        container.style.maxWidth = `${infoWindowMaxWidth}px`;
        container.style.boxSizing = 'border-box';
        container.style.padding = `${4 * sm}px ${2 * sm}px`;

        const title = document.createElement('strong');
        title.textContent = restaurant.posName || restaurant.posId;
        title.style.fontSize = `${14 * sm}px`;
        container.appendChild(title);

        const restaurantPosArea = getChainRestaurantPosArea(restaurant);
        if (restaurantPosArea) {
            const address = document.createElement('div');
            address.style.marginTop = `${4 * sm}px`;
            address.style.fontSize = `${12 * sm}px`;
            address.textContent = restaurantPosArea;
            container.appendChild(address);
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = chooseButtonLabelRef.current;
        button.style.marginTop = `${8 * sm}px`;
        button.style.padding = `${4 * sm}px 0`;
        button.style.border = 'none';
        button.style.backgroundColor = 'transparent';
        button.style.color = primaryColorRef.current;
        button.style.cursor = 'pointer';
        button.style.fontSize = `${13 * sm}px`;
        button.style.fontWeight = '600';
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            selectRestaurant(restaurant, 'map_info_window');
        });
        container.appendChild(button);

        return container;
    }, [selectRestaurant, sizeMultiplier, infoWindowMaxWidth]);

    const layout = useMemo(() => {
        const sm = sizeMultiplier;
        const closeReserve = 56 * sm;
        const maxWhiteBoxHeight = Math.max(0, storyDimensions.containerHeight - closeReserve);

        const dialogPadding = 32 * sm;
        const titleHeight = Math.ceil(18 * sm * 1.35);
        const formHeight = (8 + 40) * sm;
        const mapTopMargin = 12 * sm;
        const listTopMargin = 8 * sm;
        const errorBlockHeight = searchError ? (12 + 18) * sm : 0;
        const hasMap = allRestaurants.length > 0;
        const hasList = displayedRestaurants.length > 0;

        const fixedHeight = dialogPadding + titleHeight + formHeight + errorBlockHeight
            + (hasMap ? mapTopMargin : 0)
            + (hasList ? listTopMargin : 0);

        const preferredMapHeight = 300 * sm;
        const preferredListHeight = 2 * 80 * sm;
        const minListHeight = 80 * sm;
        const minMapHeight = 120 * sm;

        let mapHeight = hasMap ? preferredMapHeight : 0;
        let listMaxHeight = hasList ? preferredListHeight : 0;
        const availableForMapAndList = maxWhiteBoxHeight - fixedHeight;

        if (hasMap && hasList) {
            if (availableForMapAndList >= preferredMapHeight + preferredListHeight) {
                mapHeight = preferredMapHeight;
                listMaxHeight = preferredListHeight;
            } else if (availableForMapAndList >= preferredMapHeight + minListHeight) {
                mapHeight = preferredMapHeight;
                listMaxHeight = availableForMapAndList - preferredMapHeight;
            } else if (availableForMapAndList >= minMapHeight + minListHeight) {
                mapHeight = Math.min(preferredMapHeight, availableForMapAndList - minListHeight);
                listMaxHeight = availableForMapAndList - mapHeight;
            } else {
                listMaxHeight = Math.max(minListHeight, availableForMapAndList - minMapHeight);
                mapHeight = Math.max(0, availableForMapAndList - listMaxHeight);
            }
        } else if (hasMap) {
            mapHeight = Math.min(preferredMapHeight, Math.max(minMapHeight, availableForMapAndList));
        } else if (hasList) {
            listMaxHeight = Math.max(minListHeight, Math.min(preferredListHeight, availableForMapAndList));
        }

        return { maxWhiteBoxHeight, mapHeight, listMaxHeight };
    }, [
        sizeMultiplier,
        storyDimensions.containerHeight,
        searchError,
        allRestaurants.length,
        displayedRestaurants.length,
    ]);

    useEffect(() => {
        if (opened) {
            analyticsManager.logOpenPopupTag(storyId, 'chain_restaurant_dialog', true);
        }
    }, [opened, storyId]);

    useEffect(() => {
        if (!opened) {
            setLocationQuery('');
            setNearbyRestaurants([]);
            setSearchError(null);
            setIsSearching(false);
            setHasSearched(false);
            setSearchCenter(null);
            markersRef.current.forEach((marker) => {
                marker.map = null;
            });
            markersRef.current = [];
            mapRef.current = null;
            infoWindowRef.current = null;
        }
    }, [opened]);

    useEffect(() => {
        if (!opened || !mapContainerRef.current || !window.google?.maps?.importLibrary) {
            return;
        }

        let cancelled = false;

        async function initMap() {
            try {
                const { Map, InfoWindow } = await window.google.maps.importLibrary('maps');
                const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary('marker');
                if (cancelled || !mapContainerRef.current) {
                    return;
                }

                if (!mapRef.current) {
                    const center = getChainRestaurantsCenter(allRestaurants);
                    mapRef.current = new Map(mapContainerRef.current, {
                        center,
                        zoom: 6,
                        mapId: 'DEMO_MAP_ID',
                        clickableIcons: false,
                        mapTypeControl: false,
                        scaleControl: false,
                        streetViewControl: false,
                        rotateControl: false,
                        fullscreenControl: false,
                        zoomControl: false,
                        cameraControl: false,
                    });
                    infoWindowRef.current = new InfoWindow({
                        maxWidth: infoWindowMaxWidth,
                    });
                } else {
                    infoWindowRef.current.setOptions({ maxWidth: infoWindowMaxWidth });
                }

                markersRef.current.forEach((marker) => {
                    marker.map = null;
                });
                markersRef.current = [];

                for (const restaurant of mapRestaurants) {
                    const pinElement = new PinElement({ scale: mapMarkerScale });
                    const marker = new AdvancedMarkerElement({
                        map: mapRef.current,
                        position: { lat: restaurant.lat, lng: restaurant.lng },
                        content: pinElement.element,
                        gmpClickable: true,
                        title: restaurant.posName || restaurant.posId,
                    });
                    marker.addListener('click', () => {
                        analyticsManager.logChainRestaurantMarkerTag(storyId);
                        infoWindowRef.current.close();
                        infoWindowRef.current.setOptions({ maxWidth: infoWindowMaxWidth });
                        infoWindowRef.current.setContent(buildInfoWindowContent(restaurant));
                        infoWindowRef.current.open(mapRef.current, marker);
                    });
                    markersRef.current.push(marker);
                }

                fitMapToChainRestaurants(mapRef.current, mapRestaurants, searchCenter);
            } catch (error) {
                // Carte non bloquante si l'API Maps échoue
            }
        }

        initMap();

        return () => {
            cancelled = true;
        };
    }, [opened, mapRestaurants, allRestaurants, searchCenter, buildInfoWindowContent, storyId, mapMarkerScale, infoWindowMaxWidth]);

    useEffect(() => {
        if (!opened || !mapRef.current || !window.google?.maps) {
            return;
        }
        window.google.maps.event.trigger(mapRef.current, 'resize');
        fitMapToChainRestaurants(mapRef.current, mapRestaurants, searchCenter);
    }, [opened, layout.mapHeight, mapRestaurants, searchCenter]);

    async function searchNearbyRestaurantsFromCoordinates(coordinates) {
        setSearchCenter(coordinates);
        const results = findNearbyChainRestaurants(story?.chainRestaurants, coordinates.lat, coordinates.lng, 20);
        setHasSearched(true);
        setNearbyRestaurants(results);
        if (results.length === 0) {
            setSearchError(t('ChainNoRestaurantNearby'));
        } else {
            setSearchError(null);
        }
        return results;
    }

    function getGeolocationErrorMessage(error) {
        if (error?.code === 'unsupported') {
            return t('ChainGeolocationUnsupported');
        }
        if (error?.code === 1) {
            return t('ChainGeolocationDenied');
        }
        if (error?.code === 2) {
            return t('ChainGeolocationUnavailable');
        }
        if (error?.code === 3) {
            return t('ChainGeolocationTimeout');
        }
        return t('ChainGeolocationError');
    }

    async function searchNearbyRestaurants() {
        setIsSearching(true);
        setSearchError(null);
        setNearbyRestaurants([]);

        try {
            const coordinates = await geocodeCityOrPostalCode(locationQuery);
            if (coordinates == null) {
                analyticsManager.logChainRestaurantSearchCityTag(storyId, false);
                setSearchError(t('ChainGeocodeError'));
                return;
            }
            await searchNearbyRestaurantsFromCoordinates(coordinates);
            analyticsManager.logChainRestaurantSearchCityTag(storyId, true);
        } catch (error) {
            analyticsManager.logChainRestaurantSearchCityTag(storyId, false);
            setSearchError(t('ChainGeocodeError'));
        } finally {
            setIsSearching(false);
        }
    }

    async function searchFromUserLocation(e) {
        e.preventDefault();
        if (isSearching) {
            return;
        }
        setIsSearching(true);
        setSearchError(null);
        setNearbyRestaurants([]);

        try {
            const coordinates = await getUserGeolocation();
            await searchNearbyRestaurantsFromCoordinates(coordinates);
            setLocationQuery('');
            analyticsManager.logChainRestaurantSearchGeolocationTag(storyId, true);
        } catch (error) {
            analyticsManager.logChainRestaurantSearchGeolocationTag(storyId, false);
            setSearchError(getGeolocationErrorMessage(error));
        } finally {
            setIsSearching(false);
        }
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (isSearching) {
            return;
        }
        if (!locationQuery.trim()) {
            analyticsManager.logChainRestaurantResetSearchTag(storyId);
            setSearchError(null);
            setNearbyRestaurants([]);
            setHasSearched(false);
            setSearchCenter(null);
            return;
        }
        searchNearbyRestaurants();
    }

    return (
        <DialogContainer
            opened={opened}
            fadeIn
            animationKey="chain-restaurant"
            onClose={handleClose}
            closeButton={
                <IconButton
                    aria-label={t('Close')}
                    onClick={handleClose}
                    style={{
                        background: '#fff',
                        borderRadius: '50%',
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CloseIcon style={{ color: '#808080', fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier }} />
                </IconButton>
            }
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    padding: dialogPadding,
                    width: '100%',
                    maxHeight: layout.maxWhiteBoxHeight,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <DialogTitle style={{ padding: 0, fontSize: 18 * sizeMultiplier, minWidth: 0 }}>{t('ChainChooseRestaurant')}</DialogTitle>
                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        marginTop: 8 * sizeMultiplier,
                        padding: 0,
                        gap: 0,
                        border: '1px solid #ccc',
                        borderRadius: 8,
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <button
                        type="button"
                        aria-label={t('ChainGeolocation')}
                        disabled={isSearching}
                        onClick={searchFromUserLocation}
                        style={{
                            flexShrink: 0,
                            alignSelf: 'stretch',
                            width: 40 * sizeMultiplier,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 0,
                            padding: 0,
                            border: 'none',
                            outline: 'none',
                            borderRadius: 0,
                            boxSizing: 'border-box',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            backgroundColor: 'transparent',
                            color: primaryColor,
                            opacity: isSearching ? 0.6 : 1,
                            cursor: isSearching ? 'wait' : 'pointer',
                        }}
                    >
                        <MyLocationIcon htmlColor={primaryColor} style={{ fontSize: 20 * sizeMultiplier, display: 'block' }} />
                    </button>
                    <input
                        type="text"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        placeholder={t('ChainLocationPlaceholder')}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 16 * sizeMultiplier,
                            padding: `${8 * sizeMultiplier}px ${8 * sizeMultiplier}px ${8 * sizeMultiplier}px 0`,
                            margin: 0,
                            border: 'none',
                            outline: 'none',
                            backgroundColor: 'transparent',
                            boxSizing: 'border-box',
                        }}
                    />
                    <button
                        type="submit"
                        aria-label={isSearching ? t('ChainSearching') : t('ChainSearchRestaurants')}
                        disabled={isSearching}
                        style={{
                            flexShrink: 0,
                            alignSelf: 'stretch',
                            width: 40 * sizeMultiplier,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 0,
                            padding: 0,
                            border: 'none',
                            outline: 'none',
                            borderRadius: 0,
                            boxSizing: 'border-box',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            backgroundColor: primaryColor,
                            color: secondaryColor,
                            opacity: isSearching ? 0.6 : 1,
                            cursor: isSearching ? 'wait' : 'pointer',
                        }}
                    >
                        <SearchIcon htmlColor={secondaryColor} style={{ fontSize: 18 * sizeMultiplier, display: 'block' }} />
                    </button>
                </form>
                {allRestaurants.length > 0 && (
                    <div
                        ref={mapContainerRef}
                        className="chain-restaurant-map"
                        style={{
                            marginTop: 12 * sizeMultiplier,
                            marginLeft: -dialogPadding,
                            marginRight: -dialogPadding,
                            width: `calc(100% + ${dialogPadding * 2}px)`,
                            height: layout.mapHeight,
                            overflow: 'hidden',
                            backgroundColor: '#f0f0f0',
                            ['--chain-map-ui-scale']: sizeMultiplier,
                            ['--chain-map-info-max-width']: `${infoWindowMaxWidth}px`,
                        }}
                    />
                )}
                {searchError && (
                    <div style={{ marginTop: 12 * sizeMultiplier, fontSize: 14 * sizeMultiplier, color: '#666' }}>
                        {searchError}
                    </div>
                )}
                {displayedRestaurants.length > 0 && (
                    <List style={{ marginTop: 8 * sizeMultiplier, maxHeight: layout.listMaxHeight, overflowY: 'auto', flexShrink: 0, minWidth: 0, padding: 0 }}>
                        {displayedRestaurants.map((restaurant) => (
                            <ListItem
                                key={`${restaurant.posId}-${restaurant.posName}-${restaurant.lat}`}
                                disablePadding
                                style={{
                                    borderRadius: 8,
                                    alignItems: 'flex-start',
                                    paddingTop: 4 * sizeMultiplier,
                                    paddingBottom: 4 * sizeMultiplier,
                                }}
                            >
                                <div style={{ width: '100%' }}>
                                    <div
                                        style={{
                                            fontSize: 16 * sizeMultiplier,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {restaurant.posName || restaurant.posId}
                                    </div>
                                    {getChainRestaurantPosArea(restaurant) && (
                                        <div style={{ fontSize: 12 * sizeMultiplier, color: '#666', marginTop: 2 * sizeMultiplier }}>
                                            {getChainRestaurantPosArea(restaurant)}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginTop: 4 * sizeMultiplier,
                                        }}
                                    >
                                        {restaurant.distanceKm != null && (
                                            <span style={{ fontSize: 12 * sizeMultiplier, color: '#666' }}>
                                                {t('ChainRestaurantDistance', { distance: restaurant.distanceKm.toFixed(1) })}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => selectRestaurant(restaurant, 'list')}
                                            style={{
                                                marginLeft: 'auto',
                                                padding: `${4 * sizeMultiplier}px 0`,
                                                border: 'none',
                                                background: 'none',
                                                color: primaryColor,
                                                fontSize: 12 * sizeMultiplier,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontFamily: 'Roboto, sans-serif',
                                            }}
                                        >
                                            {t('ChainChooseButton')}
                                        </button>
                                    </div>
                                </div>
                            </ListItem>
                        ))}
                    </List>
                )}
                {fromDemoFeed && displayedRestaurants.length > 0 && (
                    <div
                        style={{
                            marginTop: 10 * sizeMultiplier,
                            fontSize: 12 * sizeMultiplier,
                            fontStyle: 'italic',
                            fontWeight: 400,
                            lineHeight: 1.35,
                            color: '#666666',
                            textAlign: 'center',
                        }}
                    >
                        {t('ChainDemoVersionHint')}
                    </div>
                )}
            </div>
        </DialogContainer>
    );
}

export default ChainRestaurantPlaceDialog;
